import React, { useState } from 'react';
import { Segment, Header, Icon, Button, Label, Table } from 'semantic-ui-react';
import SignedImage from './SignedImage';
import ConfirmModal from './ConfirmModal';
import '../../styles/landing-pages.css';
/**
 * BookEntryList — Displays completed books in the prototype.
 * 
 * Each book shows: title, PDF status, cover status, with edit/remove actions.
 *
 * Props:
 *   books        — array of book entries (from Redux or local state)
 *   onEditBook   — callback(book) to populate BookEntryForm for editing
 *   onRemoveBook — callback(book) to remove a book
 *   disabled     — boolean (locked prototype)
 *   onAddBook    — optional callback; when provided, renders an "Add Book" button
 *                  in the header (used to grow a published LitGroup)
 *   addLabel     — optional label for the add button (default "Add Book")
 *   live         — boolean; when true, renders the LIVE LitGroup books view
 *                  (Status/Cover columns) instead of the draft PrototypeBook view
 *                  (PDF/Cover-alias columns)
 *   importStatus — optional (bookUuid) => 'importing' | 'imported' | null, plus an optional
 *                  percent, so a row can say where its own background import has got to
 */
const BookEntryList = ({ books = [], onEditBook, onRemoveBook, disabled = false, onAddBook, addLabel = 'Add Book', live = false, removeConfirmMessage, userId, importStatus }) => {
  // The book awaiting a remove confirmation, or null. Ours rather than window.confirm — the
  // series message is several sentences about what survives the removal, and a native confirm
  // renders all of it as one undifferentiated run of text.
  const [pendingRemove, setPendingRemove] = useState(null);

  if (books.length === 0) return null;

  const showActions = !!(onEditBook || onRemoveBook);

  return (
    <Segment>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Header as="h4">
          <Icon name={live ? 'book' : 'list'} />
          <Header.Content>
            {live ? 'Books in this Series' : 'Books in this Literary Group'}
            <Header.Subheader>
              {books.length} book{books.length !== 1 ? 's' : ''} {live ? 'live in the group' : 'added'}
            </Header.Subheader>
          </Header.Content>
        </Header>
        {onAddBook && (
          <Button size="tiny" primary icon="plus" content={addLabel} onClick={onAddBook} />
        )}
      </div>

      <Table compact size="small" basic="very" celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>#</Table.HeaderCell>
            <Table.HeaderCell width={live ? (showActions ? 4 : 5) : (showActions ? 5 : 7)}>Book Title</Table.HeaderCell>
            {live && <Table.HeaderCell width={2}>Author</Table.HeaderCell>}
            <Table.HeaderCell width={live ? 3 : 4}>{live ? 'Status' : 'PDF'}</Table.HeaderCell>
            <Table.HeaderCell width={live ? 3 : 4}>Cover</Table.HeaderCell>
            {showActions && <Table.HeaderCell width={2}>Actions</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {books.map((book, index) => {
            const hasCover = live
              ? !!(book.coverUrl || book.coverFileName)
              : !!(book.coverFileName || book.coverAlias);
            const hasPdf = !!(book.fileName || book.pdfAlias);

            return (
              <Table.Row key={book.uuid || book.localId || index}>
                <Table.Cell>{index + 1}</Table.Cell>
                <Table.Cell className='book-title-width'>
                  <strong>{book.bookTitle || book.fileName?.replace(/\.pdf$/i, '') || 'Untitled'}</strong>
                  {/* The row's own state, not the group's. "added" is only true for the seconds
                      between the Book node existing and its import starting; leaving it there for
                      the whole eleven-minute pipeline told the operator nothing about which book
                      the progress bar was for, or whether this one was still working. */}
                  {(() => {
                    const st = live && importStatus ? importStatus(book.uuid) : null;
                    if (st?.state === 'importing') {
                      return (
                        <Label size="mini" color="yellow" style={{ marginLeft: '0.5em' }}>
                          <Icon className="loading-icon" name="cog" loading />
                          {typeof st.percent === 'number' ? `importing ${st.percent}%` : 'importing'}
                        </Label>
                      );
                    }
                    if (st?.state === 'imported') {
                      return (
                        <Label size="mini" color="green" style={{ marginLeft: '0.5em' }}>
                          <Icon name="check" />imported
                        </Label>
                      );
                    }
                    if (live && book.addedToExistingLitGroup) {
                      return <Label size="mini" color="teal" style={{ marginLeft: '0.5em' }}>added</Label>;
                    }
                    return null;
                  })()}
                </Table.Cell>
                {live && (
                  <Table.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                      <SignedImage
                        s3Path={book.authorImageS3Path}
                        userId={userId}
                        alt={book.authorName || 'Author'}
                        size="tiny"
                        rounded
                        width="34px"
                        height="34px"
                        fallbackIcon="user circle"
                      />
                      {book.authorName && (
                        <span style={{ fontSize: '0.85em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {book.authorName}
                        </span>
                      )}
                    </div>
                  </Table.Cell>
                )}
                <Table.Cell>
                  {live ? (
                    <Label size="mini" color={(book.status || 'Active') === 'Active' ? 'green' : 'grey'}>
                      <Icon name={(book.status || 'Active') === 'Active' ? 'check circle' : 'archive'} />
                      {book.status || 'Active'}
                    </Label>
                  ) : hasPdf ? (
                    <Label size="mini" color="green">
                      <Icon name="file pdf outline" />
                      {book.fileName || book.pdfAlias}
                    </Label>
                  ) : (
                    <Label size="mini" color="red">
                      <Icon name="warning" /> Missing
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {hasCover ? (
                    <Label size="mini" color="green">
                      <Icon name="image" />
                      {live ? 'Cover' : (book.coverFileName || book.coverAlias)}
                    </Label>
                  ) : (
                    <Label size="mini" color="yellow">
                      <Icon name="image outline" /> None
                    </Label>
                  )}
                </Table.Cell>
                {showActions && (
                  <Table.Cell>
                    <Button.Group size="mini">
                      {onEditBook && (
                        <Button
                          icon="edit"
                          basic
                          color="blue"
                          onClick={() => onEditBook(book)}
                          disabled={disabled}
                          title="Edit this book"
                        />
                      )}
                      {onRemoveBook && (
                        <Button
                          icon="trash"
                          basic
                          color="red"
                          onClick={() => setPendingRemove(book)}
                          disabled={disabled}
                          title={live ? 'Remove this book from the series' : 'Remove this book'}
                        />
                      )}
                    </Button.Group>
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <ConfirmModal
        open={!!pendingRemove}
        title={live ? 'Remove this book from the series?' : 'Remove this book?'}
        items={pendingRemove ? [pendingRemove.bookTitle || pendingRemove.fileName || 'Untitled'] : []}
        note={pendingRemove && removeConfirmMessage ? removeConfirmMessage(pendingRemove) : null}
        confirmLabel="Remove"
        onCancel={() => setPendingRemove(null)}
        onConfirm={() => {
          const book = pendingRemove;
          setPendingRemove(null);
          if (book && onRemoveBook) onRemoveBook(book);
        }}
      >
        {live
          ? 'The book is hidden from the group.'
          : 'The book is taken off this Literary Group draft.'}
      </ConfirmModal>
    </Segment>
  );
};

export default BookEntryList;
